/* The sheets, each one something the product actually does.
 *
 * The artboard also draws join, notifications, leave, export and a recovery
 * phrase. None exists, so none is here; see reports/surfaces/customer-app.md.
 */
import { type StoredSecret, plural, t } from "@damga/kit";
import { useState } from "react";

import type { Card } from "./App";
import { CodeDigits, Sheet, SheetFacts } from "./ui";

export function CodeSheet({ secret, onClose }: { secret: StoredSecret | null; onClose: () => void }) {
  return (
    <Sheet onClose={onClose} eyebrow={t("sheet.code.eyebrow")} title={t("code.title")}>
      <div className="sheet-block">
        <CodeDigits secret={secret} />
        <p className="notice">{t("code.works_offline")}</p>
      </div>
      <div className="sheet-actions">
        <button type="button" className="btn btn-primary" onClick={onClose}>
          {t("common.close")}
        </button>
      </div>
    </Sheet>
  );
}

/* REDEEM IS THE CODE, SHOWN FOR A REASON. The cashier picks the reward on the
 * till and keys the customer's code; `redemption.py` refuses without it and
 * refuses a code that is not this card's owner. So the sheet's sentence is
 * exactly what happens, and nothing is marked used from the phone. */
export function RedeemSheet({
  card,
  secret,
  onClose,
}: {
  card: Card;
  secret: StoredSecret | null;
  onClose: () => void;
}) {
  return (
    <Sheet onClose={onClose} eyebrow={t("sheet.code.eyebrow")} title={card.reward_description}>
      <p className="sheet-body">{t("sheet.redeem.body")}</p>
      <div className="sheet-block">
        <CodeDigits secret={secret} />
      </div>
      <SheetFacts facts={[{ k: card.business_name, v: plural("wallet.readyCount", card.claimable) }]} />
      <div className="sheet-actions">
        <button type="button" className="btn btn-primary" onClick={onClose}>
          {t("common.close")}
        </button>
      </div>
    </Sheet>
  );
}

export function NameSheet({
  initial,
  onSave,
  onClose,
}: {
  initial: string | null;
  onSave: (name: string) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(initial ?? "");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const valid = name.trim().length >= 2;

  return (
    <Sheet onClose={onClose} eyebrow={t("sheet.account")} title={t("profile.name.title")}>
      <p className="sheet-body">{t("profile.name.body")}</p>
      <form
        className="sheet-block"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!valid) return;
          setBusy(true);
          setFailed(false);
          try {
            await onSave(name.trim());
          } catch {
            setFailed(true);
          } finally {
            setBusy(false);
          }
        }}
      >
        <label className="field">
          <span className="field-label">{t("settings.name")}</span>
          <input
            className="input"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setFailed(false);
            }}
            autoComplete="name"
            maxLength={80}
          />
        </label>
        {failed && (
          <p className="error" role="alert">
            {t("email.failed")}
          </p>
        )}
        <div className="sheet-actions">
          <button type="submit" className="btn btn-primary" disabled={busy || !valid}>
            {busy ? t("common.loading") : t("email.save")}
          </button>
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            {t("email.notNow")}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

const LOOKS_LIKE_EMAIL = /^[^@\s]+@[^@\s.]+(\.[^@\s.]+)+$/;

/* NOTHING SENDS A CONFIRMATION YET, so this sheet does not promise one. The
 * artboard's "we send one link to confirm it" is not true today: there is no
 * mail sender behind this, and the settings row says the address is added and
 * not confirmed. */
export function EmailSheet({
  initial,
  onSave,
  onClose,
}: {
  initial: string | null;
  onSave: (email: string) => Promise<void>;
  onClose: () => void;
}) {
  const [email, setEmail] = useState(initial ?? "");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const trimmed = email.trim();
  const valid = LOOKS_LIKE_EMAIL.test(trimmed);
  const showInvalid = touched && trimmed.length > 0 && !valid;

  return (
    <Sheet onClose={onClose} eyebrow={t("sheet.account")} title={t("email.title")}>
      <p className="sheet-body">{t("email.body")}</p>
      <form
        className="sheet-block"
        onSubmit={async (e) => {
          e.preventDefault();
          setTouched(true);
          if (!valid) return;
          setBusy(true);
          setFailed(false);
          try {
            await onSave(trimmed);
          } catch {
            setFailed(true);
          } finally {
            setBusy(false);
          }
        }}
      >
        <label className="field">
          <span className="field-label">{t("email.label")}</span>
          <input
            className="input"
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setFailed(false);
            }}
            onBlur={() => setTouched(true)}
            autoComplete="email"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            maxLength={254}
            aria-invalid={showInvalid || undefined}
          />
        </label>
        {showInvalid && (
          <p className="error" role="alert">
            {t("email.invalid")}
          </p>
        )}
        {failed && (
          <p className="error" role="alert">
            {t("email.failed")}
          </p>
        )}
        <div className="sheet-actions">
          <button type="submit" className="btn btn-primary" disabled={busy || !valid}>
            {busy ? t("common.loading") : t("email.save")}
          </button>
          <button type="button" className="btn btn-quiet" onClick={onClose}>
            {t("email.notNow")}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

/* THE UPDATES SHEET, whose contents the phone works out (see notices.ts). The
 * artboard's "Damga only messages you about your own cards" is not here: Damga
 * messages nobody, and the sheet says where these came from instead. */
export function UpdatesSheet({
  notices,
  onMarkRead,
  onClose,
}: {
  notices: { id: string; text: string }[];
  onMarkRead: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet onClose={onClose} eyebrow={t("shell.updates")} title={t("updates.title")}>
      {notices.length === 0 ? (
        <div className="sheet-block">
          <p className="sheet-body">{t("updates.empty.title")}</p>
          <p className="fine">{t("updates.empty.body")}</p>
        </div>
      ) : (
        <>
          <ul className="sheet-list">
            {notices.map((n) => (
              <li key={n.id}>{n.text}</li>
            ))}
          </ul>
          <p className="fine">{t("updates.note")}</p>
        </>
      )}
      <div className="sheet-actions">
        <button type="button" className="btn btn-primary" onClick={notices.length ? onMarkRead : onClose}>
          {notices.length ? t("updates.markRead") : t("common.close")}
        </button>
      </div>
    </Sheet>
  );
}

/* DOWNLOAD YOUR DATA, against GET /me/export. The artboard promises a file "sent
 * to your email within an hour"; there is no mail sender, and the route answers
 * now, so the sheet says now and the file arrives in the browser's downloads. */
export function ExportSheet({
  onDownload,
  onClose,
}: {
  onDownload: () => Promise<void>;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <Sheet onClose={onClose} eyebrow={t("settings.yourData")} title={t("export.title")}>
      <p className="sheet-body">{t("export.body")}</p>
      <p className="fine">{t("export.note")}</p>
      {failed && (
        <p className="error" role="alert">
          {t("export.failed")}
        </p>
      )}
      <div className="sheet-actions">
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setFailed(false);
            try {
              await onDownload();
            } catch {
              setFailed(true);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? t("common.loading") : t("export.confirm")}
        </button>
        <button type="button" className="btn btn-quiet" onClick={onClose}>
          {t("shell.notNow")}
        </button>
      </div>
    </Sheet>
  );
}

/* WHO SEES WHAT, and every sentence is a response the counter actually gets.
 * Checked on 2026-09-17: the stamp's success body carries the display name and
 * `mask_identity`'s last four digits; search is scoped to one shop and masked;
 * no merchant route returns another shop's card. */
export function WhoSeesSheet({ onClose }: { onClose: () => void }) {
  return (
    <Sheet onClose={onClose} eyebrow={t("settings.yourData")} title={t("settings.whoSees")}>
      <ul className="sheet-list">
        <li>{t("who.body")}</li>
        <li>{t("who.search")}</li>
        <li>{t("who.visits")}</li>
        <li>{t("who.notOthers")}</li>
      </ul>
      <div className="sheet-actions">
        <button type="button" className="btn btn-primary" onClick={onClose}>
          {t("common.close")}
        </button>
      </div>
    </Sheet>
  );
}

export function SignOutSheet({
  cards,
  who,
  hasPasskey,
  onConfirm,
  onClose,
}: {
  cards: number;
  who: string;
  hasPasskey: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Sheet onClose={onClose} eyebrow={t("sheet.account")} title={t("signout.title")}>
      <p className="sheet-body">
        {t("signout.body")} {hasPasskey ? t("settings.signOutNotePasskey") : t("settings.signOutNote")}
      </p>
      <SheetFacts
        facts={[
          { k: t("signout.cards"), v: String(cards) },
          ...(who ? [{ k: t("signout.as"), v: who }] : []),
        ]}
      />
      <div className="sheet-actions">
        <button type="button" className="btn btn-primary" onClick={onConfirm}>
          {t("settings.signOut")}
        </button>
        <button type="button" className="btn btn-quiet" onClick={onClose}>
          {t("shell.notNow")}
        </button>
      </div>
    </Sheet>
  );
}

/* DELETE, AND IT IS IMMEDIATE. The artboard promises a 24-hour grace period;
 * `DELETE /me` has none, so none is promised. What stays is said, because a
 * promise with an asterisk needs the asterisk: each shop's record of visits,
 * with no name on it, and backups for thirty days. */
export function EraseSheet({
  cards,
  onConfirm,
  onClose,
}: {
  cards: Card[];
  onConfirm: () => Promise<void>;
  onClose: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const live = cards.filter((c) => c.program_type !== "points");
  const stamps = live.reduce((n, c) => n + c.balance, 0);
  const shops = new Set(cards.map((c) => c.business_id)).size;

  return (
    <Sheet onClose={onClose} eyebrow={t("sheet.careful")} title={t("erase.title")} danger>
      <p className="sheet-body">{t("erase.body")}</p>
      {shops > 0 && (
        <p className="sheet-body">
          {t("erase.tally", "en", {
            stamps: plural("erase.stampCount", stamps),
            shops: plural("erase.shopCount", shops),
          })}
        </p>
      )}
      <p className="label">{t("erase.keepsTitle")}</p>
      <ul className="sheet-list">
        <li>{t("erase.keepsShops")}</li>
        <li>{t("erase.keepsBackups")}</li>
      </ul>
      {failed && (
        <p className="error" role="alert">
          {t("error.generic.title")}
        </p>
      )}
      <div className="sheet-actions">
        <button
          type="button"
          className="btn btn-danger"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setFailed(false);
            try {
              await onConfirm();
            } catch {
              setFailed(true);
              setBusy(false);
            }
          }}
        >
          {busy ? t("common.loading") : t("erase.confirm")}
        </button>
        <button type="button" className="btn btn-quiet" onClick={onClose} disabled={busy}>
          {plural("erase.cancel", cards.length)}
        </button>
      </div>
    </Sheet>
  );
}
